"use client";

import { AnimatePresence, m as motion, useInView, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import {
  CalendarCheck,
  CheckCircle2,
  ChevronLeft,
  Inbox,
  Send,
  ShieldAlert,
  ShoppingBag,
} from "lucide-react";
import type { PhoneDemo as PhoneDemoData } from "@/content/data/service-details";

// Simulazione di un telefono che riceve avvisi da un bot (stile chat Telegram, tema scuro).
// Scena in loop: schermata di blocco → notifica → si apre la chat → i messaggi entrano uno alla volta.
// I colori della chat sono fissi apposta: è un dispositivo, non segue il tema del sito.

const ICONS = {
  lead: { Icon: Inbox, color: "#5eb5f7" },
  call: { Icon: CalendarCheck, color: "#4fd18b" },
  alert: { Icon: ShieldAlert, color: "#f5b94a" },
  order: { Icon: ShoppingBag, color: "#c58af9" },
  ok: { Icon: CheckCircle2, color: "#4fd18b" },
} as const;

type Frame = { screen: "lock" | "notif" | "chat"; shown: number; typing: boolean };

const LOCK: Frame = { screen: "lock", shown: 0, typing: false };

// Tempi in ms dall'inizio del ciclo.
function buildTimeline(count: number): [number, Frame][] {
  const steps: [number, Frame][] = [
    [0, LOCK],
    [900, { screen: "notif", shown: 0, typing: false }],
    [3300, { screen: "chat", shown: 0, typing: true }],
  ];
  let at = 3300;
  for (let i = 1; i <= count; i++) {
    at += 900;
    steps.push([at, { screen: "chat", shown: i, typing: false }]);
    if (i < count) {
      at += 1500;
      steps.push([at, { screen: "chat", shown: i, typing: true }]);
    }
  }
  return steps;
}

const HOLD_MS = 4500;

export function PhoneDemo({ bot, notification, messages }: PhoneDemoData) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: "-80px" });
  const reduce = useReducedMotion();
  const [frame, setFrame] = useState<Frame>(LOCK);
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    if (reduce || !inView) return;
    const steps = buildTimeline(messages.length);
    const timers = steps.map(([at, f]) => setTimeout(() => setFrame(f), at));
    const end = steps[steps.length - 1][0] + HOLD_MS;
    timers.push(setTimeout(() => setCycle((c) => c + 1), end));
    return () => timers.forEach(clearTimeout);
  }, [inView, reduce, cycle, messages.length]);

  // Chi ha le animazioni ridotte vede direttamente la chat completa.
  const f: Frame = reduce ? { screen: "chat", shown: messages.length, typing: false } : frame;

  const label = `Esempio: ${bot} manda avvisi sul telefono. ${messages.map((msg) => msg.title).join(", ")}.`;

  return (
    <div ref={ref} className="relative flex flex-col items-center select-none">
      <div
        className="pointer-events-none absolute top-1/4 h-64 w-64 rounded-full blur-3xl opacity-25"
        style={{ background: "hsl(var(--primary))" }}
      />

      {/* Sui telefoni piccoli scala all'88%: stesse proporzioni, meno ingombro.
          La scala sta su un wrapper perché il transform del motion.div serve alla vibrazione. */}
      <div className="relative h-[458px] w-[225px] sm:h-[520px] sm:w-[256px] xl:h-[560px] xl:w-[276px]">
        <div className="origin-top-left scale-[0.88] sm:scale-100">
          <motion.div
            role="img"
            aria-label={label}
            animate={f.screen === "notif" ? { rotate: [0, -1.5, 1.5, -1, 1, 0] } : { rotate: 0 }}
            transition={{ duration: 0.5 }}
            className="relative h-[520px] w-[256px] rounded-[44px] border border-white/10 bg-[#0b0d10] p-[9px] shadow-[0_24px_80px_-20px_hsl(var(--primary)/0.45)] xl:h-[560px] xl:w-[276px]"
          >
            <div aria-hidden="true" className="relative h-full w-full overflow-hidden rounded-[36px] bg-[#0e1621]">
              {/* Dynamic island */}
              <div className="absolute left-1/2 top-2 z-30 h-[22px] w-[84px] -translate-x-1/2 rounded-full bg-black" />

              <AnimatePresence initial={false}>
                {f.screen !== "chat" ? (
                  <motion.div
                    key="lock"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, scale: 1.04 }}
                    transition={{ duration: 0.45 }}
                    className="absolute inset-0 flex flex-col items-center px-3 pt-16 text-white"
                    style={{
                      background:
                        "radial-gradient(120% 70% at 50% 0%, hsl(var(--primary) / 0.55), transparent 60%), linear-gradient(180deg, #0d2a33 0%, #081218 100%)",
                    }}
                  >
                    <p className="text-[13px] font-medium text-white/75">lunedì</p>
                    <p className="font-sans text-[62px] font-light leading-none tracking-tight">09:41</p>

                    <AnimatePresence>
                      {f.screen === "notif" && (
                        <motion.div
                          key="notif"
                          initial={{ opacity: 0, y: -18, scale: 0.96 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ type: "spring", stiffness: 260, damping: 22 }}
                          className="relative mt-8 w-full overflow-hidden rounded-2xl bg-white/15 p-3 backdrop-blur-md"
                        >
                          <div className="flex items-center gap-2">
                            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-[#2aabee]">
                              <Send className="h-3.5 w-3.5 -translate-x-px text-white" />
                            </span>
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-white/70">
                              Telegram
                            </span>
                            <span className="ml-auto text-[11px] text-white/55">ora</span>
                          </div>
                          <p className="mt-2 text-[13px] font-semibold">{bot}</p>
                          <p className="text-[12.5px] leading-snug text-white/85">{notification}</p>
                          {/* Tocco sulla notifica */}
                          <motion.span
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: [0, 0.5, 0], scale: [0, 1, 1.6] }}
                            transition={{ delay: 1.7, duration: 0.6 }}
                            className="pointer-events-none absolute left-1/2 top-1/2 -ml-8 -mt-8 h-16 w-16 rounded-full bg-white"
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <div className="absolute bottom-2 left-1/2 h-1 w-28 -translate-x-1/2 rounded-full bg-white/60" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="chat"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="absolute inset-0 flex flex-col text-white"
                  >
                    {/* Intestazione chat */}
                    <div className="flex items-center gap-2.5 bg-[#17212b] px-3 pb-2.5 pt-10">
                      <ChevronLeft className="h-5 w-5 text-[#6ab3f3]" />
                      <span
                        className="flex h-8 w-8 items-center justify-center rounded-full text-[13px] font-bold"
                        style={{ background: "linear-gradient(135deg, hsl(var(--primary)), #2aabee)" }}
                      >
                        {bot.charAt(0)}
                      </span>
                      <div className="leading-tight">
                        <p className="text-[13px] font-semibold">{bot}</p>
                        <p className="text-[11px] text-white/50">bot</p>
                      </div>
                    </div>

                    {/* Messaggi: ancorati in basso, i più vecchi scorrono su come in una chat vera */}
                    <div className="flex flex-1 flex-col justify-end gap-2 overflow-hidden px-2.5 pb-3">
                      <motion.span
                        layout="position"
                        className="mx-auto mb-1 rounded-full bg-white/10 px-2.5 py-0.5 text-[10.5px] text-white/70"
                      >
                        Oggi
                      </motion.span>
                      <AnimatePresence initial={false}>
                        {messages.slice(0, f.shown).map((msg, i) => {
                          const { Icon, color } = ICONS[msg.icon];
                          return (
                            <motion.div
                              key={msg.title}
                              layout="position"
                              initial={{ opacity: 0, y: 10, scale: 0.97 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              transition={{ duration: 0.35, ease: "easeOut" }}
                              className="max-w-[92%]"
                            >
                              <div className="rounded-2xl rounded-tl-md bg-[#182533] px-3 py-2">
                                <p className="flex items-center gap-1.5 text-[12.5px] font-semibold">
                                  <Icon className="h-3.5 w-3.5 shrink-0" style={{ color }} />
                                  {msg.title}
                                </p>
                                <p className="mt-0.5 text-[12px] leading-snug text-white/85">{msg.text}</p>
                                <p className="mt-0.5 text-right text-[10px] text-white/40">09:4{2 + i}</p>
                              </div>
                              {msg.actions && msg.actions.length > 0 && (
                                <div className="mt-1 grid grid-cols-2 gap-1">
                                  {msg.actions.map((a) => (
                                    <span
                                      key={a}
                                      className="rounded-lg bg-white/[0.08] py-1.5 text-center text-[11px] font-medium text-[#6ab3f3]"
                                    >
                                      {a}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </motion.div>
                          );
                        })}
                        {f.typing && (
                          <motion.div
                            key="typing"
                            layout="position"
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="flex w-fit gap-1 rounded-2xl rounded-tl-md bg-[#182533] px-3 py-2.5"
                          >
                            {[0, 1, 2].map((d) => (
                              <motion.span
                                key={d}
                                animate={{ y: [0, -3, 0] }}
                                transition={{ duration: 0.6, repeat: Infinity, delay: d * 0.15 }}
                                className="h-1.5 w-1.5 rounded-full bg-white/60"
                              />
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Barra di scrittura */}
                    <div className="flex items-center gap-2 bg-[#17212b] px-3 pb-5 pt-2">
                      <span className="flex-1 rounded-full bg-[#0e1621] px-3 py-1.5 text-[11.5px] text-white/35">
                        Messaggio
                      </span>
                      <Send className="h-4 w-4 text-[#6ab3f3]" />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>

      <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
        Esempio di avvisi automatici
      </p>
    </div>
  );
}
